if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = this.getCartTarget();
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (this.cart && (this.cart.tagName === 'CART-DRAWER' || this.cart.tagName === 'CART-ADDED-POPUP') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) this.submitButton.setAttribute('aria-disabled', true);
        if (this.submitButton) this.submitButton.classList.add('loading');
        const spinnerShow = this.querySelector('.loading__spinner');
        if (spinnerShow) spinnerShow.classList.remove('hidden');

        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Minimal payload: only params Shopify /cart/add.js accepts (extra fields can cause 502)
        const variantInput = this.form.querySelector('[name="id"]');
        const variantId = variantInput ? variantInput.value : '';
        if (!variantId) {
          this.handleErrorMessage('Please choose a variant.');
          if (this.submitButton) this.submitButton.removeAttribute('aria-disabled');
          const spinnerEarly = this.querySelector('.loading__spinner');
          if (spinnerEarly) spinnerEarly.classList.add('hidden');
          if (this.submitButton) this.submitButton.classList.remove('loading');
          return;
        }
        const quantityInput = this.form.querySelector('[name="quantity"]') || (this.form.id && document.querySelector('input[name="quantity"][form="' + this.form.id + '"]'));
        const quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', String(quantity));
        let sellingPlanValue = this.form.querySelector('[name="selling_plan"]') && this.form.querySelector('[name="selling_plan"]').value;
        if (!sellingPlanValue && this.dataset.sectionId) {
          const productInfo = document.getElementById('ProductInfo-' + this.dataset.sectionId);
          const subscriptionWidget = productInfo && productInfo.querySelector('[data-subscription-choice]');
          const subscribeRadio = subscriptionWidget && subscriptionWidget.querySelector('[data-subscription-radio][value="subscribe"]:checked');
          const frequencySelect = subscriptionWidget && subscriptionWidget.querySelector('[data-frequency-select]');
          if (subscribeRadio && frequencySelect && frequencySelect.value) {
            sellingPlanValue = frequencySelect.value;
          }
        }
        if (sellingPlanValue) {
          formData.append('selling_plan', sellingPlanValue);
        }
        const fullFormData = new FormData(this.form);
        for (const [key] of fullFormData) {
          if (key.startsWith('properties[')) {
            formData.append(key, fullFormData.get(key));
          }
        }
        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const cartAddJsonUrl = cartAddUrl.endsWith('.js') ? cartAddUrl : cartAddUrl + '.js';

        fetch(cartAddJsonUrl, config)
          .then((response) => {
            return response.text().then((text) => {
              if (!response.ok) {
                const err = new Error(response.statusText || 'Could not add to cart');
                err.status = response.status;
                err.body = text;
                throw err;
              }
              if (!text || !text.trim()) {
                throw new Error('Empty response from server');
              }
              try {
                return JSON.parse(text);
              } catch (e) {
                throw new Error('Invalid response from server');
              }
            });
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              if (window.routes && window.routes.cart_url) window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            const isClientError = e && e.status >= 400 && e.status < 500;
            if (!isClientError && this.cart) {
              this.tryRefreshCartAndOpenDrawer();
              return;
            }
            this.error = true;
            let message = typeof e === 'string' ? e : (e && e.message) ? e.message : 'Could not add to cart. Please try again.';
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                message: message,
              });
            }
            this.handleErrorMessage(message);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            const spinnerHide = this.querySelector('.loading__spinner');
            if (spinnerHide) spinnerHide.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      tryRefreshCartAndOpenDrawer() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const refreshAndOpen = () => fetch(cartUrl + '?section_id=cart-drawer')
          .then((response) => response.text())
          .then((htmlText) => {
            const html = new DOMParser().parseFromString(htmlText, 'text/html');
            // Replace all of #CartDrawer so shipping bar, upsells, items, and footer all render
            // (matches onCartUpdate logic in cart.js)
            const cartDrawerEl = document.querySelector('cart-drawer');
            const freshCartDrawer = html.querySelector('cart-drawer');
            const cartDrawerDiv = document.querySelector('#CartDrawer');
            const freshCartDrawerDiv = html.querySelector('#CartDrawer');
            if (cartDrawerDiv && freshCartDrawerDiv) {
              cartDrawerDiv.innerHTML = freshCartDrawerDiv.innerHTML;
              const overlay = document.querySelector('#CartDrawer-Overlay');
              if (overlay && cartDrawerEl) overlay.addEventListener('click', cartDrawerEl.close.bind(cartDrawerEl));
            }
            if (freshCartDrawer && cartDrawerEl) {
              cartDrawerEl.classList.toggle('is-empty', freshCartDrawer.classList.contains('is-empty'));
            }
            return fetch(cartUrl + '?section_id=cart-icon-bubble').then((r) => r.text());
          })
          .then((iconHtmlText) => {
            const html = new DOMParser().parseFromString(iconHtmlText, 'text/html');
            const bubbleSection = html.querySelector('#cart-icon-bubble .shopify-section') || html.querySelector('#cart-icon-bubble') || html.querySelector('.shopify-section');
            const targetBubble = document.getElementById('cart-icon-bubble');
            if (bubbleSection && targetBubble) targetBubble.innerHTML = bubbleSection.innerHTML;
          })
          .then(() => {
            this.error = false;
            this.handleErrorMessage(false);
            try {
              if (this.cart && typeof this.cart.open === 'function') this.cart.open();
              if (typeof document.dispatchEvent === 'function') {
                document.dispatchEvent(new CustomEvent('cart:refresh'));
              }
            } catch (err) {
              console.error('Cart drawer open:', err);
            }
          })
          .catch((err) => {
            console.error(err);
            this.error = true;
            this.handleErrorMessage('The store is temporarily unavailable. Try "One time purchase" or try again in a moment.');
          });
        // Delay so the add has time to persist before we fetch updated cart HTML (fixes empty drawer on first add)
        setTimeout(refreshAndOpen, 400);
      }

      getCartTarget() {
        const popup = document.querySelector('cart-added-popup');
        const isProductCard = this.form && this.form.closest('.card-wrapper, .product-card-wrapper, .card-product, .product-card-ooo, .collection-product-grid__product-card, .product-item, [data-product-card], quick-add-modal');
        if (popup && isProductCard) return popup;
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
